import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { GraduationCap, BookOpen, School, Award, LogOut, TrendingUp, Users, Settings, Home, UserCheck, Crown, Library, Pencil, AlertCircle, Menu, X, Brain, Sparkles, Target, Zap, Atom } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import EdpiesLogo from '@/components/EdpiesLogo';
import FeedbackButton from '@/components/FeedbackButton';
import PromotionalBanner from '@/components/PromotionalBanner';
import { getStageFromYear } from '../config/taxonomy';

const CategorySelection = ({ progress, updateProgress }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const featureMode = location.state?.feature; // 'practice-library', 'challenger', etc.
  const subscription = progress?.user?.subscription;
  const userType = progress?.user?.userType || 'student';
  const isApproved = progress?.user?.isApproved !== false; // Default to true for existing users
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Get user's year group for filtering categories
  const userYearGroup = progress?.user?.studentProfile?.yearGroup || 
                        progress?.user?.yearGroup || 
                        null;

  // Get user's stage for conditional rendering
  const userStage = userYearGroup ? getStageFromYear(userYearGroup) : null;

  // Redirect students away from this almost-empty hub. Since 7+/11+/GCSE/KS3
  // students all have a dedicated `/welcome` Home Dashboard now, this page
  // only ever rendered a useful A-Level card grid — for everyone else it was
  // a blank "Welcome to Edpies — choose your stage" page with no options.
  // Tutors and parents still benefit from this view, so the redirect is
  // scoped to students who aren't A-Level.
  const isALevelStudent = userStage === 'ks5' ||
                          /year[\s-]?12|year[\s-]?13|a[\s-]?level/i.test(userYearGroup || '');
  useEffect(() => {
    if (userType === 'student' && !isALevelStudent) {
      navigate('/welcome', { replace: true });
    }
  }, [userType, isALevelStudent, navigate]);

  // Check if user should see KS2/11+ content
  const showKS2Content = !userStage || ['ks1', 'ks2-lower', 'ks2-upper', '11-plus', '7-plus'].includes(userStage);

  // STEM Library — only for Year 9-10 / GCSE students (flat-rate audience).
  // Permissive matching: handles "Year 9", "year-9", "Year 9 (Age 13-14)", "ks4", "gcse".
  const isGcseStage = userStage === 'ks4' ||
                      /year[- ]?(9|10|11)/i.test(userYearGroup || '') ||
                      String(userYearGroup || '').toLowerCase().includes('gcse') ||
                      String(userYearGroup || '').toLowerCase().includes('ks4');

  // Check if user is approved on mount
  useEffect(() => {
    if (!isApproved) {
      setShowBlockedModal(true);
    }
  }, [isApproved]);

  // Handle blocked modal close - logout and go to home
  const handleBlockedModalClose = () => {
    setShowBlockedModal(false);
    navigate('/');
    setTimeout(() => {
      localStorage.clear();
      updateProgress(null);
    }, 100);
  };

  // Navigation helper that closes mobile menu
  const handleNavigation = (path) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  const allCategories = [
    {
      id: 'a-levels',
      title: 'Eds A-Level Hub™',
      description: 'Study • Practice • UniGuide',
      icon: Award,
      gradient: 'from-orange-500 to-red-500',
      subjects: ['Maths', 'Physics', 'Chemistry', 'Biology', 'Economics', 'Computer Science', 'English Lit', 'History'],
      isHub: true,
      stages: ['a-level']
    }
  ];

  // A-Levels is the only category now - no filtering needed
  const categories = allCategories;

  const selectCategory = (categoryId) => {
    updateProgress({
      selectedCategory: categoryId
    });
    
    // Map category ID to stage for routes
    const categoryToStage = {
      'seven-plus': '7-plus',
      'eleven-plus': '11-plus',
      'year-9': 'ks3',
      'year-10': 'ks4',
      'gcse': 'gcse',
      'a-levels': 'a-levels'
    };
    const stage = categoryToStage[categoryId] || categoryId;
    
    // Handle different feature modes from ExploreLanding
    if (featureMode === 'practice-library') {
      // Navigate to subject selection for practice library
      navigate(`/subjects/${categoryId}`, { state: { feature: 'practice-library' } });
    } else if (featureMode === 'challenger') {
      // Navigate to subject selection for challenger
      navigate(`/subjects/${categoryId}`, { state: { feature: 'challenger' } });
    } else if (categoryId === 'a-levels') {
      // Redirect A-Levels to the new Eds A-Level Hub
      navigate('/a-level-hub');
    } else {
      navigate(`/subjects/${categoryId}`);
    }
  };

  const handleLogout = () => {
    // Navigate first, then clear state to prevent route guard redirect
    navigate('/');
    // Use setTimeout to ensure navigation completes before clearing auth
    setTimeout(() => {
      localStorage.clear();
      updateProgress(null);
    }, 100);
  };

  return (
    <div className="min-h-screen p-4 md:p-8" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      {/* Account Pending Approval Modal */}
      <Dialog open={showBlockedModal} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-10 h-10 text-amber-600" />
              </div>
            </div>
            <DialogTitle className="text-2xl font-bold text-center">Account Pending Approval</DialogTitle>
            <DialogDescription className="text-center text-base mt-4">
              Thank you for registering! Our team will get back to you soon with access to all our learning resources.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-6 p-4 bg-purple-50 rounded-lg border border-purple-100">
            <p className="text-sm text-purple-700 text-center">
              We're reviewing your registration and will notify you once your account is activated.
            </p>
          </div>
          <div className="mt-6 flex justify-center">
            <Button 
              onClick={handleBlockedModalClose}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
              data-testid="blocked-modal-close"
            >
              Got it, thanks!
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="max-w-7xl mx-auto space-y-6 md:space-y-8 animate-fade-in">
        {/* Top Bar - Mobile Optimized with Hamburger Menu */}
        <div className="flex items-center justify-between">
          {/* Logo & Brand - Always visible */}
          <div className="flex items-center gap-3">
            <EdpiesLogo size="md" />
            <span className="text-white font-bold text-lg hidden sm:block">Edpies</span>
          </div>
          
          {/* Desktop Navigation - Hidden on mobile */}
          <div className="hidden md:flex items-center gap-2">
            {/* Role-specific buttons */}
            {userType === 'tutor' && (
              <Button
                onClick={() => navigate('/admin/questions')}
                className="bg-gradient-to-r from-slate-700 to-slate-900 hover:from-slate-800 hover:to-slate-950 text-white flex items-center gap-2 text-sm"
                data-testid="admin-nav-button"
              >
                <Settings className="h-4 w-4" />
                Admin Panel
              </Button>
            )}
            {userType === 'parent' && (
              <Button
                onClick={() => navigate('/parent-dashboard')}
                className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white flex items-center gap-2 text-sm"
                data-testid="parent-dashboard-button"
              >
                <UserCheck className="h-4 w-4" />
                Track Progress
              </Button>
            )}
            
            <Button
              onClick={() => navigate('/welcome')}
              variant="ghost"
              className="text-white hover:bg-white/20 flex items-center gap-1.5 text-sm px-3"
              data-testid="home-button"
            >
              <Home className="h-4 w-4" />
              Home
            </Button>
            
            {userType === 'student' && (
              <>
                <Button
                  onClick={() => navigate('/student-dashboard')}
                  variant="ghost"
                  className="text-white hover:bg-white/20 flex items-center gap-1.5 text-sm px-3"
                  data-testid="dashboard-button"
                >
                  <TrendingUp className="h-4 w-4" />
                  Progress
                </Button>
                <Button
                  onClick={() => navigate('/elibrary')}
                  className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white flex items-center gap-1.5 text-sm px-3"
                  data-testid="elibrary-button"
                >
                  <Library className="h-4 w-4" />
                  Eds-Library
                </Button>
                {isGcseStage && (
                  <Button
                    onClick={() => navigate('/stem-library')}
                    className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white flex items-center gap-1.5 text-sm px-3"
                    data-testid="stem-library-button"
                  >
                    <Atom className="h-4 w-4" />
                    STEM
                  </Button>
                )}
                <Button
                  onClick={() => navigate('/tutor-papers')}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white flex items-center gap-1.5 text-sm px-3"
                  data-testid="tutor-papers-button"
                >
                  <GraduationCap className="h-4 w-4" />
                  Tutor Papers
                </Button>
                {/* Whiteboard X — hidden until Pro feature is fully built; will surface inside Pro tier later */}
              </>
            )}
            
            {userType === 'parent' && (
              <>
                <Button
                  onClick={() => navigate('/subscription')}
                  className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white flex items-center gap-1.5 text-sm px-3"
                  data-testid="subscription-button"
                >
                  <Crown className="h-4 w-4" />
                  Upgrade
                </Button>
                <Button
                  onClick={() => navigate('/tutor-collaboration')}
                  className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white flex items-center gap-1.5 text-sm px-3"
                  data-testid="tutor-nav-button"
                >
                  <Users className="h-4 w-4" />
                  Find Tutor
                </Button>
              </>
            )}
            
            {userType === 'tutor' && (
              <>
                <Button
                  onClick={() => navigate('/whiteboard-x/create')}
                  className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white flex items-center gap-1.5 text-sm px-3"
                  data-testid="create-challenge-button"
                >
                  <Pencil className="h-4 w-4" />
                  Create Challenge
                </Button>
                <Button
                  onClick={() => navigate('/tutor-dashboard')}
                  className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white flex items-center gap-1.5 text-sm px-3"
                  data-testid="tutor-dashboard-button"
                >
                  <Users className="h-4 w-4" />
                  My Profile
                </Button>
              </>
            )}
            
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="text-white hover:bg-white/20 flex items-center gap-1.5 text-sm px-3"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
            <FeedbackButton 
              userType={userType} 
              userName={progress?.user?.name || ''} 
              userEmail={progress?.user?.email || ''} 
              variant="nav"
            />
          </div>
          
          {/* Mobile Menu Button */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-white hover:bg-white/20"
                data-testid="mobile-menu-button"
              >
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 bg-gradient-to-b from-purple-700 to-indigo-800 border-none">
              <SheetHeader className="border-b border-white/20 pb-4">
                <SheetTitle className="text-white flex items-center gap-2">
                  <EdpiesLogo size="sm" />
                  Menu
                </SheetTitle>
              </SheetHeader>
              <div className="flex flex-col gap-2 mt-6">
                {/* Home */}
                <Button
                  onClick={() => handleNavigation('/welcome')}
                  variant="ghost"
                  className="w-full justify-start text-white hover:bg-white/20"
                >
                  <Home className="h-5 w-5 mr-3" />
                  Home
                </Button>
                
                {/* Student Menu Items */}
                {userType === 'student' && (
                  <>
                    <Button
                      onClick={() => handleNavigation('/student-dashboard')}
                      variant="ghost"
                      className="w-full justify-start text-white hover:bg-white/20"
                    >
                      <TrendingUp className="h-5 w-5 mr-3" />
                      My Progress
                    </Button>
                    <Button
                      onClick={() => handleNavigation('/my-journey')}
                      className="w-full justify-start bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white"
                    >
                      <Brain className="h-5 w-5 mr-3" />
                      My Learning Journey
                    </Button>
                    <Button
                      onClick={() => handleNavigation('/elibrary')}
                      className="w-full justify-start bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"
                    >
                      <Library className="h-5 w-5 mr-3" />
                      Eds-Library
                    </Button>
                    {isGcseStage && (
                      <Button
                        onClick={() => handleNavigation('/stem-library')}
                        className="w-full justify-start bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white"
                      >
                        <Atom className="h-5 w-5 mr-3" />
                        STEM Library
                      </Button>
                    )}
                    <Button
                      onClick={() => handleNavigation('/tutor-papers')}
                      className="w-full justify-start bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                    >
                      <GraduationCap className="h-5 w-5 mr-3" />
                      Tutor Papers
                    </Button>
                    {/* Whiteboard X — hidden until Pro feature is fully built */}
                  </>
                )}
                
                {/* Parent Menu Items */}
                {userType === 'parent' && (
                  <>
                    <Button
                      onClick={() => handleNavigation('/parent-dashboard')}
                      className="w-full justify-start bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white"
                    >
                      <UserCheck className="h-5 w-5 mr-3" />
                      Track Child's Progress
                    </Button>
                    <Button
                      onClick={() => handleNavigation('/subscription')}
                      className="w-full justify-start bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white"
                    >
                      <Crown className="h-5 w-5 mr-3" />
                      Upgrade Plan
                    </Button>
                    <Button
                      onClick={() => handleNavigation('/tutor-collaboration')}
                      className="w-full justify-start bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
                    >
                      <Users className="h-5 w-5 mr-3" />
                      Find a Tutor
                    </Button>
                  </>
                )}
                
                {/* Tutor Menu Items */}
                {userType === 'tutor' && (
                  <>
                    <Button
                      onClick={() => handleNavigation('/admin/questions')}
                      className="w-full justify-start bg-gradient-to-r from-slate-700 to-slate-900 hover:from-slate-800 hover:to-slate-950 text-white"
                    >
                      <Settings className="h-5 w-5 mr-3" />
                      Admin Panel
                    </Button>
                    <Button
                      onClick={() => handleNavigation('/whiteboard-x/create')}
                      className="w-full justify-start bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white"
                    >
                      <Pencil className="h-5 w-5 mr-3" />
                      Create Challenge
                    </Button>
                    <Button
                      onClick={() => handleNavigation('/tutor-dashboard')}
                      className="w-full justify-start bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
                    >
                      <Users className="h-5 w-5 mr-3" />
                      My Tutor Profile
                    </Button>
                  </>
                )}
                
                {/* Divider */}
                <div className="border-t border-white/20 my-2"></div>
                
                {/* Feedback Button */}
                <FeedbackButton 
                  userType={userType} 
                  userName={progress?.user?.name || ''} 
                  userEmail={progress?.user?.email || ''} 
                  variant="default"
                />
                
                {/* Logout */}
                <Button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleLogout();
                  }}
                  variant="ghost"
                  className="w-full justify-start text-white hover:bg-white/20"
                >
                  <LogOut className="h-5 w-5 mr-3" />
                  Logout
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
        
        {/* Header */}
        <div className="text-center space-y-3 md:space-y-4">
          <div className="flex justify-center mb-4 md:mb-6">
            <EdpiesLogo size="xl" className="shadow-2xl" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            {userType === 'tutor' ? 'Tutor Dashboard' : 'Welcome to Edpies'}
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-white/90 max-w-2xl mx-auto px-2">
            {userType === 'tutor' 
              ? 'Manage your tutor profile, create questions, and connect with students'
              : 'Choose your education stage to access comprehensive mock papers and practice materials'
            }
          </p>
        </div>

        {/* Promotional Banner for new users */}
        {subscription?.isPromotional && (
          <div className="mt-6 max-w-4xl mx-auto">
            <PromotionalBanner subscription={subscription} userName={progress?.user?.name?.split(' ')[0]} />
          </div>
        )}

        {/* Show Category Cards only for students and parents, not tutors */}
        {userType !== 'tutor' ? (
          <>

            {/* KS2 11+ GL Assessment card removed — students now take the
                adaptive assessment from a single canonical entry point inside
                the dashboard, avoiding two confusingly-similar paths. */}

            {/* A-Level Hub Banner - Only visible for A-Level students */}
            {isALevelStudent && categories.length > 0 && (
              <>
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-white/80 flex items-center gap-2">
                    <Award className="h-5 w-5" />
                    A-Level Hub
                    <span className="text-sm font-normal text-gray-400">- Study, Practice & UniGuide</span>
                  </h2>
                </div>
                <div className="grid grid-cols-1 gap-4 md:gap-6">
                  {categories.map((category) => {
                    const IconComponent = category.icon;
                    return (
                      <Card
                        key={category.id}
                        className="shadow-xl md:shadow-2xl hover:shadow-2xl md:hover:shadow-3xl transition-all duration-300 hover:-translate-y-1 md:hover:-translate-y-2 cursor-pointer border-2 hover:border-white group"
                        onClick={() => selectCategory(category.id)}
                        data-testid={`category-${category.id}`}
                      >
                        <CardHeader className={`bg-gradient-to-r ${category.gradient} text-white rounded-t-lg p-4 md:p-6`}>
                          <div className="flex items-center gap-3 md:gap-4">
                            <div className="bg-white/20 p-2 md:p-3 rounded-xl group-hover:scale-110 transition-transform">
                              <IconComponent className="h-6 w-6 md:h-10 md:w-10" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <CardTitle className="text-xl md:text-3xl font-bold mb-1 md:mb-2 truncate">{category.title}</CardTitle>
                              <CardDescription className="text-white/90 text-sm md:text-base line-clamp-2">
                                {category.description}
                              </CardDescription>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="p-4 md:p-6">
                          <div className="space-y-2 md:space-y-3">
                            <p className="font-semibold text-gray-700 text-sm md:text-base">Available Subjects:</p>
                            <div className="flex flex-wrap gap-1.5 md:gap-2">
                              {category.subjects.map((subject, idx) => (
                                <span
                                  key={idx}
                                  className="px-2 md:px-3 py-0.5 md:py-1 bg-gray-100 text-gray-700 rounded-full text-xs md:text-sm font-medium"
                                >
                                  {subject}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="mt-4 md:mt-6 flex items-center justify-end text-purple-600 font-semibold group-hover:gap-3 gap-2 transition-all text-sm md:text-base">
                            Explore <span className="text-xl md:text-2xl">→</span>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </>
            )}
          </>
        ) : (
          /* Tutor Dashboard View */
          <div className="grid md:grid-cols-2 gap-6 mt-12">
            <Card
              className="shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-2 cursor-pointer border-2 hover:border-white group"
              onClick={() => navigate('/tutor-dashboard')}
              data-testid="tutor-profile-card"
            >
              <CardHeader className="bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-t-lg">
                <div className="flex items-center gap-4">
                  <div className="bg-white/20 p-3 rounded-xl group-hover:scale-110 transition-transform">
                    <Users className="h-10 w-10" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-3xl font-bold mb-2">My Tutor Profile</CardTitle>
                    <CardDescription className="text-white/90 text-base">
                      Manage your profile, set rates, and view inquiries
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-3">
                  <p className="font-semibold text-gray-700">Quick Actions:</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">Edit Profile</span>
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">View Inquiries</span>
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">Subscription</span>
                  </div>
                </div>
                <div className="mt-6 flex items-center justify-end text-green-600 font-semibold group-hover:gap-3 gap-2 transition-all">
                  Manage Profile <span className="text-2xl">→</span>
                </div>
              </CardContent>
            </Card>

            <Card
              className="shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-2 cursor-pointer border-2 hover:border-white group"
              onClick={() => navigate('/admin/questions')}
              data-testid="question-bank-card"
            >
              <CardHeader className="bg-gradient-to-r from-slate-700 to-slate-900 text-white rounded-t-lg">
                <div className="flex items-center gap-4">
                  <div className="bg-white/20 p-3 rounded-xl group-hover:scale-110 transition-transform">
                    <Settings className="h-10 w-10" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-3xl font-bold mb-2">Question Bank</CardTitle>
                    <CardDescription className="text-white/90 text-base">
                      Create and manage practice questions for students
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-3">
                  <p className="font-semibold text-gray-700">Features:</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">Add Questions</span>
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">Review Bank</span>
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">Export</span>
                  </div>
                </div>
                <div className="mt-6 flex items-center justify-end text-slate-600 font-semibold group-hover:gap-3 gap-2 transition-all">
                  Open Panel <span className="text-2xl">→</span>
                </div>
              </CardContent>
            </Card>

            <Card
              className="shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-2 cursor-pointer border-2 hover:border-white group"
              onClick={() => navigate('/tutor-collaboration')}
              data-testid="marketplace-card"
            >
              <CardHeader className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-t-lg">
                <div className="flex items-center gap-4">
                  <div className="bg-white/20 p-3 rounded-xl group-hover:scale-110 transition-transform">
                    <GraduationCap className="h-10 w-10" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-3xl font-bold mb-2">Tutor Marketplace</CardTitle>
                    <CardDescription className="text-white/90 text-base">
                      View your public profile as students and parents see it
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-3">
                  <p className="font-semibold text-gray-700">See how you appear:</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">Public Profile</span>
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">Other Tutors</span>
                  </div>
                </div>
                <div className="mt-6 flex items-center justify-end text-purple-600 font-semibold group-hover:gap-3 gap-2 transition-all">
                  View Marketplace <span className="text-2xl">→</span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Floating 1:1 Tutoring Button - Show for Parents only */}
      {userType === 'parent' && (
        <button
          onClick={() => navigate('/tutor-collaboration')}
          className="fixed bottom-20 left-6 bg-gradient-to-r from-green-500 to-emerald-500 text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-transform z-40 flex items-center gap-2 group"
          data-testid="floating-tutor-btn"
        >
          <Users className="h-6 w-6" />
          <span className="hidden group-hover:inline font-semibold whitespace-nowrap">1:1 Tutoring</span>
        </button>
      )}

      {/* Admin Button - Show for Tutors */}
      {userType === 'tutor' && (
        <button
          onClick={() => navigate('/admin/questions')}
          className="fixed bottom-20 left-6 bg-gradient-to-r from-slate-700 to-slate-900 text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-transform z-40 flex items-center gap-2 group"
          data-testid="admin-btn"
        >
          <Settings className="h-6 w-6" />
          <span className="hidden group-hover:inline font-semibold whitespace-nowrap">Question Bank</span>
        </button>
      )}
    </div>
  );
};

export default CategorySelection;
